/**
 * @file BackToTop.tsx
 * @description 返回顶部组件
 * @details
 * 1. 当页面滚动超过指定偏移量时显示返回顶部按钮
 * 2. 点击按钮时平滑滚动到页面顶部
 * 3. 针对 iOS 设备做了特殊处理，确保滚动到顶部功能正常工作
 * 4. 支持自定义显示偏移量和样式类名
 */

import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import { IS_IOS } from '@/constants';

import Styles from './backToTop.module.scss';

export interface BackToTopProps {
  shownOffset?: number;
  className?: string;
}

export const BackToTop: FC<BackToTopProps> = (props) => {
  const { shownOffset = 0, className } = props;

  const [shown, setShown] = useState(false)

  useEffect(() => {
    const ele = document.querySelector('#__next');
    function cb(event: Event) {
      const target = event.target as HTMLDivElement;
      const dis = target.scrollTop;
      setShown(dis > shownOffset);
    }
    ele?.addEventListener('scroll', cb);
    return () => {
      ele?.removeEventListener('scroll', cb);
    }
  }, [shownOffset])

  const onClick = (e: React.TouchEvent) => {
    e.preventDefault();
    const ele = document.querySelector('#__next') as HTMLDivElement;
    const headerEle = document.querySelector('#headerContainer') as HTMLDivElement;
    if (headerEle) {
      headerEle.style.transition = 'auto';
    }
    if (IS_IOS) {
      ele?.scrollTo(0, 1)
      setTimeout(() => {
        ele?.scrollTo(0, 0)
      }, 100)
    } else {
      ele?.scrollTo(0, 0)
    }
    setTimeout(() => {
      headerEle.style.transition = 'all 0.2s ease';
    }, 300)
  }

  function smoothScrollToTop(duration: number) {
    const ele = document.querySelector('#__next') as HTMLElement | null;
    if (!ele) return;
  
    const start = ele.scrollTop;
    const change = -start;
    const startTime = performance.now();
  
    function animateScroll(currentTime: number) {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1); 
      ele!.scrollTop = start + change * easeInOutQuad(progress);
  
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    }
  
    function easeInOutQuad(t: number) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }
  
    requestAnimationFrame(animateScroll);
  }

  return (
    shown ? (
      <div
        className={cn(Styles['back-to-top'], className)}
        onClick={(e) => {
          e.stopPropagation();
          smoothScrollToTop(200);
        }}
        onTouchEnd={onClick}
      />
    ) : <></>
  )
}

